import { call, put, select, takeEvery } from "redux-saga/effects";

import * as apis from "../../../network/apis/companyPortalEventRequests";
import messages from "../../../assets/locale/messages";
import {
  getEventRequestListMeta,
  normalizeEventRequests,
  extractCheckedInSummaryFromResponse,
  isEventRequestTabCountParams,
} from "../../../components/Events/Event/Applicants/eventRequestMappers";
import {
  joiningRequestApiStatuses,
  joiningRequestRejectedApiStatuses,
} from "../../../components/Events/Event/Applicants/constants";
import {
  showErrorMsg,
  showSuccessMsg,
  showWarningMsg,
} from "../../../utils/Helpers";
import * as actions from "./actions";
import * as types from "./types";

const extractListItems = (response) => response?.data?.data ?? [];
const normalizeIds = (ids = []) =>
  ids.map((id) => {
    const numberId = Number(id);
    return Number.isFinite(numberId) ? numberId : id;
  });
const isBulkOperationPartialSuccess = (response) => response?.status === 207;

function* refreshEventRequests(listParams) {
  if (!listParams) return;
  yield put(actions.getCompanyEventRequestsRequest(listParams));
}

function* getCompanyEventRequestsSaga({ payload }) {
  try {
    const shouldUpdateTabCounts = isEventRequestTabCountParams(payload);

    yield put(
      actions.getCompanyEventRequestsResponse({
        list: [],
        meta: { currentPage: 1, count: 0, pages: 1 },
      }),
    );
    const response = yield call(apis.getCompanyPortalEventRequests, payload);
    const lang = yield select((state) => state.locale.lang);
    const sharedLabels = messages[lang]?.shared;
    const meta = getEventRequestListMeta(response);
    const list = normalizeEventRequests(
      extractListItems(response),
      lang,
      sharedLabels,
    );
    yield put(
      actions.getCompanyEventRequestsResponse({
        list,
        meta,
      }),
    );
    if (
      payload?.status === joiningRequestApiStatuses.pending &&
      shouldUpdateTabCounts
    ) {
      yield put(
        actions.getCompanyEventRequestsSummaryResponse({
          eventId: payload.event_id,
          pending: meta.count,
          loyalApplicantsCount: response.data?.loyal_applicants_count ?? 0,
          nonLoyalApplicantsCount: response.data?.non_loyal_applicants_count ?? 0,
        }),
      );
    } else if (payload?.status === joiningRequestApiStatuses.joined) {
      const checkInSummary = extractCheckedInSummaryFromResponse(response, list);
      const summaryPayload = { eventId: payload.event_id };

      if (shouldUpdateTabCounts) {
        summaryPayload.joined = meta.count;
      }
      if (checkInSummary.hasExplicitCounts) {
        summaryPayload.checkedIn = checkInSummary.checkedIn;
        summaryPayload.notCheckedIn = checkInSummary.notCheckedIn;
      }

      if (Object.keys(summaryPayload).length > 1) {
        yield put(actions.getCompanyEventRequestsSummaryResponse(summaryPayload));
      }
    } else if (
      payload?.status === joiningRequestRejectedApiStatuses &&
      shouldUpdateTabCounts
    ) {
      yield put(
        actions.getCompanyEventRequestsSummaryResponse({
          eventId: payload.event_id,
          rejected: meta.count,
        }),
      );
    } else if (
      payload?.status === joiningRequestApiStatuses.removed &&
      shouldUpdateTabCounts
    ) {
      yield put(
        actions.getCompanyEventRequestsSummaryResponse({
          eventId: payload.event_id,
          removed: meta.count,
        }),
      );
    }
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
  }
}

function* bulkApproveCompanyEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkApproveCompanyPortalEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
      role_id: payload.roleId,
      event_role_shift_id: payload.eventRoleShiftId,
    });
    const successMessage =
      response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshEventRequests, payload.listParams);
      return;
    }
    if (payload.successTitle && payload.successBody) {
      yield showSuccessMsg(payload.successBody, true, payload.successTitle);
    } else {
      yield showSuccessMsg(successMessage, true);
    }
    yield call(refreshEventRequests, payload.listParams);
    payload.onSuccess?.(response);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* bulkRejectCompanyEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkRejectCompanyPortalEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
      rejection_reason: payload.rejectionReason,
    });
    const successMessage =
      response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshEventRequests, payload.listParams);
      return;
    }
    yield showSuccessMsg(successMessage, true);
    yield call(refreshEventRequests, payload.listParams);
    payload.onSuccess?.(response);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* bulkAllocateCompanyEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkAllocateCompanyPortalEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
      zone_id: payload.zoneId,
    });
    const successMessage = response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshEventRequests, payload.listParams);
      payload.onSuccess?.(response);
      return;
    }
    yield showSuccessMsg(successMessage, true);
    yield call(refreshEventRequests, payload.listParams);
    payload.onSuccess?.(response);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* bulkUnassignCompanyEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkUnassignCompanyPortalEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
    });
    const successMessage = response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshEventRequests, payload.listParams);
      payload.onSuccess?.(response);
      return;
    }
    yield showSuccessMsg(successMessage, true);
    yield call(refreshEventRequests, payload.listParams);
    payload.onSuccess?.(response);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* bulkRemoveCompanyEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkRemoveCompanyPortalEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
      removal_reason: payload.removalReason,
    });
    const successMessage = response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshEventRequests, payload.listParams);
      payload.onSuccess?.(response);
      return;
    }
    yield showSuccessMsg(successMessage, true);
    yield call(refreshEventRequests, payload.listParams);
    payload.onSuccess?.(response);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* bulkChangeRoleCompanyEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkChangeRoleCompanyPortalEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
      role_id: payload.roleId,
      event_role_shift_id: payload.eventRoleShiftId,
    });
    const successMessage = response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshEventRequests, payload.listParams);
      payload.onSuccess?.(response);
      return;
    }
    yield showSuccessMsg(successMessage, true);
    yield call(refreshEventRequests, payload.listParams);
    payload.onSuccess?.(response);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

export default function* companyEventRequestsSagas() {
  yield takeEvery(
    types.GET_COMPANY_EVENT_REQUESTS_REQUEST,
    getCompanyEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_APPROVE_COMPANY_EVENT_REQUESTS_REQUEST,
    bulkApproveCompanyEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_REJECT_COMPANY_EVENT_REQUESTS_REQUEST,
    bulkRejectCompanyEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_ALLOCATE_COMPANY_EVENT_REQUESTS_REQUEST,
    bulkAllocateCompanyEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_UNASSIGN_COMPANY_EVENT_REQUESTS_REQUEST,
    bulkUnassignCompanyEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_REMOVE_COMPANY_EVENT_REQUESTS_REQUEST,
    bulkRemoveCompanyEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_CHANGE_ROLE_COMPANY_EVENT_REQUESTS_REQUEST,
    bulkChangeRoleCompanyEventRequestsSaga,
  );
}
